// MessageComponents.js
import { AnimatePresence, motion } from 'framer-motion';
import AudioMessagesPlayer from './AudioMessagesPlayer';
import ImageGallery from './ImageGallery';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useMessageHtml } from '../Hooks/useMessageHtml';
import './MessageComponents.css';
import { FileRender } from './FileRender';
import MessageArchiveDownload from './MessageArchiveDownload';
import { VideoRender } from './VideoRender';

export const MediaMessageUserSending = () => {
  return (
    <div className="sending-dots" aria-label="Sending…">
      <span></span>
      <span></span>
      <span></span>
    </div>
  );
};

const notSentView = {
  label: 'Not sent',
  initial: { opacity: 0, scale: 0.4 },
  animate: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 500, damping: 16 } },
  icon: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <rect x="7" y="2.75" width="2" height="7" rx="1" fill="currentColor" />
      <circle cx="8" cy="12.25" r="1.25" fill="currentColor" />
    </svg>
  ),
};

const deliveryStatusViews = {
  sending: {
    label: 'Sending…',
    initial: { opacity: 0, scale: 0.6 },
    animate: { opacity: 1, scale: 1, transition: { delay: 0.4, duration: 0.2, ease: 'easeOut' } },
    icon: (
      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
        <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <line
          className="delivery-clock-hand delivery-clock-hand--hour"
          x1="8"
          y1="8"
          x2="10.5"
          y2="8"
        />
        <line
          className="delivery-clock-hand delivery-clock-hand--minute"
          x1="8"
          y1="8"
          x2="8"
          y2="4.25"
        />
      </svg>
    ),
  },
  failed: notSentView,
  // The server refused what the message carries; it says why instead of offering a Retry.
  rejected: notSentView,
};

export const MessageDeliveryStatus = ({ status, onRetry }) => {
  const view = deliveryStatusViews[status];
  const canRetry = status === 'failed' && Boolean(onRetry);
  const Tag = canRetry ? motion.button : motion.span;
  const a11yProps = canRetry
    ? { type: 'button', onClick: onRetry, 'aria-label': 'Retry sending' }
    : { role: 'img', 'aria-label': view?.label };

  return (
    <AnimatePresence>
      {view && (
        <Tag
          key={status}
          className={`message-delivery-status message-delivery-status--${status}`}
          {...a11yProps}
          initial={view.initial}
          animate={view.animate}
          exit={{ opacity: 0, scale: 0.6, transition: { duration: 0.15 } }}
          {...(canRetry && { whileHover: { scale: 1.1 }, whileTap: { scale: 0.92 } })}
        >
          {view.icon}
        </Tag>
      )}
    </AnimatePresence>
  );
};

export const MessageRetryButton = ({ onRetry }) => (
  <AnimatePresence>
    {onRetry && (
      <motion.div
        className="message-retry"
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, transition: { duration: 0.15 } }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        <button type="button" className="message-retry-button" onClick={onRetry}>
          Retry
          <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
        </button>
      </motion.div>
    )}
  </AnimatePresence>
);

// Why the server refused a message, where a failed one offers a Retry.
export const MessageRejection = ({ text }) => (
  <AnimatePresence>
    {text && (
      <motion.div
        className="message-rejection"
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, transition: { duration: 0.15 } }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        <span className="message-rejection-text" role="status">
          {text}
        </span>
      </motion.div>
    )}
  </AnimatePresence>
);

// 1. Assistant header message (intro message)
// (This one is left intact because its wrapper is separate.)
export const AssistantHeaderMessage = ({ message }) => {
  return (
    <motion.div className="assistant-header" exit={{ display: 'none' }}>
      {message.type === 'bot' ? (
        <>
          {message.replacebotlogo === 'yes' ? (
            <img className="avatar" src={message.logo} alt={message.newassistant} />
          ) : (
            <motion.div
              className="avatar bot"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15, duration: 0.1 }}
            />
          )}
          <div className="assistant-info">
            <div className={message.showbotbadge === 'yes' ? 'bot-info' : 'name'}>
              {message.newassistant}
            </div>
            <div className="assistant-subtitle">{message.subscription}</div>
          </div>
        </>
      ) : (
        <>
          <img className="avatar" src={message.logo} alt={message.newassistant} />
          <div className="assistant-info">
            <div className="name">{message.newassistant}</div>
            <div className="assistant-subtitle">{message.subscription}</div>
          </div>
        </>
      )}
    </motion.div>
  );
};

/**
 * For all media and HTML components, we now return only the internal markup
 * that goes inside the message-bubble div.
 */

// 2. Media message from bot – URL
export const MediaMessageBotURL = ({ message }) => {
  const { rehypePlugins } = useMessageHtml();
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <a className="link-wrapper" href={message.url} target="_blank" rel="noopener noreferrer">
        <div className="link-container">
          <img className="link-image" src={message.image} alt={message.url} />
          <div className="link-description">
            <span className="description-title">{message.title}</span>
            <div className="description-text">{message.description}</div>
          </div>
        </div>
      </a>
    </>
  );
};

// 3. Media message from bot – Image (single or gallery)
export const MediaMessageBotImage = ({ message }) => {
  const { rehypePlugins } = useMessageHtml();
  return (
    <>
      <ImageGallery message={message} />
      <MessageArchiveDownload images={message.images} />
      {message.text && (
        <div className="gallery-message">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={rehypePlugins}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer">
                  {props.children}
                </a>
              ),
            }}
          >
            {typeof message.text === 'string'
              ? message.text
              : JSON.stringify(message.text, null, 2)}
          </ReactMarkdown>
        </div>
      )}
    </>
  );
};

// 4. Media message from bot – Audio
// export const MediaMessageBotAudio = ({ message }) => {
//     const dataUrlSrc = `data:audio/mp4;base64,${message.audioBinary}`;
//   return (
//     <AudioMessagesPlayer
//       audioSrc={dataUrlSrc}
//       histogramData={message.histogram || []}
//       playbackTime={message.playbackTime}
//     />
//   );
// };

// test for mobiles
export const MediaMessageBotAudio = ({ message }) => {
  const audioSrc =
    message.audio.base64 || message.audioBlobUrl || message.file || message.audioDataUrl;
  return (
    <AudioMessagesPlayer
      audioSrc={audioSrc}
      histogramData={message.histogram || message.audio.histogram || []}
      playbackTime={message.playbackTime || message.audio.playback_time}
    />
  );
};

// 5. Media message from bot – File
export const MediaMessageBotFile = ({ message }) => {
  const { rehypePlugins } = useMessageHtml();
  return (
    <>
      {/* {message.images && message.images.length > 0 && <ImageGallery message={message} />} */}

      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <FileRender message={message} />
    </>
  );
};

// 6. Media message from bot – Video
export const MediaMessageBotVideo = ({ message }) => {
  const { rehypePlugins } = useMessageHtml();
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <VideoRender message={message} />
    </>
  );
};

const getHtmlControls = (controls) =>
  Array.isArray(controls)
    ? controls.filter((control) => control?.content_type === 'text/html' && control?.content)
    : [];

export const hasHtmlControls = (controls) => getHtmlControls(controls).length > 0;

export const MessageControls = ({ controls }) => {
  const { sanitize } = useMessageHtml();
  const htmlControls = getHtmlControls(controls);
  if (htmlControls.length === 0) return null;

  return (
    <>
      {htmlControls.map((control, index) => (
        <div key={`${control.name}-${index}`} className="message-row assistant no-badge">
          <div
            className="message-control html-content"
            dangerouslySetInnerHTML={{ __html: sanitize(control.content) }}
          />
        </div>
      ))}
    </>
  );
};

// 7. Media message from bot – HTML
export const MediaMessageBotHTML = ({ message }) => {
  const { rehypePlugins, sanitize } = useMessageHtml();
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <div className="html-content" dangerouslySetInnerHTML={{ __html: sanitize(message.code) }} />
      <span className="timestamp">{message.timestamp}</span>
    </>
  );
};

/**
 * ----------------------------
 * Outbound Messages (User)
 * -----------------x-----------
 */

// 8. Media message from user – URL
export const MediaMessageUserURL = ({ message }) => {
  const { rehypePlugins } = useMessageHtml();
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <a className="link-wrapper" href={message.url} target="_blank" rel="noopener noreferrer">
        <div className="link-container">
          <img className="link-image" src={message.image} alt={message.url} />
          <div className="link-description">
            <span className="description-title">{message.title}</span>
            <div className="description-text">
              <div className="text-container">{message.description}</div>
            </div>
          </div>
        </div>
      </a>
    </>
  );
};

// 9. Media message from user – Image (single or gallery)
export const MediaMessageUserImage = ({ message }) => {
  const { rehypePlugins } = useMessageHtml();
  return (
    <>
      <ImageGallery message={message} />
      <MessageArchiveDownload images={message.images} />
      {message.text && (
        <div className="gallery-message">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={rehypePlugins}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer">
                  {props.children}
                </a>
              ),
            }}
          >
            {typeof message.text === 'string'
              ? message.text
              : JSON.stringify(message.text, null, 2)}
          </ReactMarkdown>
        </div>
      )}
    </>
  );
};

// test for mobiles
export const MediaMessageUserAudio = ({ message }) => {
  const audioSrc = message.audioBlobUrl || message.file || message.audioDataUrl;

  return (
    <AudioMessagesPlayer
      audioSrc={audioSrc}
      histogramData={message.histogram || []}
      playbackTime={message.playbackTime}
    />
  );
};

// 11. Media message from user – File
export const MediaMessageUserFile = ({ message }) => {
  const { rehypePlugins } = useMessageHtml();
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <FileRender message={message} />
    </>
  );
};

// 12. Media message from user – Video
export const MediaMessageUserVideo = ({ message }) => {
  const { rehypePlugins } = useMessageHtml();
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <VideoRender message={message} />
    </>
  );
};

// 13. Media message from user – HTML
export const MediaMessageUserHTML = ({ message }) => {
  const { rehypePlugins, sanitize } = useMessageHtml();
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <div className="html-content" dangerouslySetInnerHTML={{ __html: sanitize(message.code) }} />
      <span className="timestamp">{message.timestamp}</span>
    </>
  );
};
